/**
 * Pages légales `/mentions-legales`, `/cgu`, `/confidentialite`, `/cookies` (DECISIONS §11, A-41).
 *
 * Le texte vient **entièrement** du catalogue `ui:legal.*`, section par section (`titre`,
 * `paragraphes`, `liste`), ce qui rend la bascule FR ↔ EN immédiate et sans rechargement. Aucune
 * phrase n’est écrite dans ce fichier : la seule chaîne littérale est un identifiant d’ancre.
 *
 * Deux marques sont affichées sur **chaque** page, comme l’exige A-41 :
 * - la **version** du document (`v1.0 — 2026-10-19`), visible et aussi exposée en `data-version` ;
 * - la mention **« texte à valider par le porteur »** : aucune page n’est présentée comme
 *   juridiquement validée tant que le porteur n’a pas relu le modèle.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Entete } from '../ui/Entete';
import { GestionCookies } from '../ui/GestionCookies';
import { PiedDePage } from '../ui/PiedDePage';

import type { TFunction } from 'i18next';

/** Identifiants des quatre pages légales (clé de catalogue et segment de route). */
export type PageLegaleId = 'mentionsLegales' | 'cgu' | 'confidentialite' | 'cookies';

/** Une section de texte légal, telle que décrite dans le catalogue. */
interface SectionLegale {
  readonly titre: string;
  readonly paragraphes?: readonly string[];
  readonly liste?: readonly string[];
}

/** Vrai si la valeur est une section exploitable (titre textuel). */
function estSection(valeur: unknown): valeur is SectionLegale {
  if (valeur === null || typeof valeur !== 'object') return false;
  return typeof (valeur as SectionLegale).titre === 'string';
}

/**
 * Lit les sections d’une page dans le catalogue.
 *
 * `returnObjects` renvoie la structure telle qu’elle est écrite en JSON ; une valeur inattendue
 * (catalogue incomplet) donne une page sans sections plutôt qu’une exception à l’écran.
 */
function lireSections(t: TFunction, cle: string): SectionLegale[] {
  const valeur: unknown = t(cle, { returnObjects: true });
  return Array.isArray(valeur) ? valeur.filter(estSection) : [];
}

/** Page légale générique, pilotée par son identifiant de catalogue. */
export function PageLegale({ page }: { readonly page: PageLegaleId }) {
  const { t } = useTranslation();
  const sections = lireSections(t, `legal.${page}.sections`);

  return (
    <div className="ecran ecran--legale">
      <Entete />
      <main className="page-legale" data-page-legale={page}>
        <p className="page-legale__retour">
          <Link to="/">{t('legal.retourAccueil')}</Link>
        </p>
        <h1 className="page-legale__titre">{t(`legal.${page}.titre`)}</h1>
        <div className="page-legale__marques">
          <p className="etiquette-legale" data-version={t('legal.version')}>
            {t('legal.etiquetteVersion')} {t('legal.version')}
          </p>
          <p className="etiquette-legale etiquette-legale--a-valider" data-a-valider="oui">
            {t('legal.aValider')}
          </p>
        </div>
        <p className="page-legale__aide">{t('legal.aValiderAide')}</p>
        <p className="page-legale__intro">{t(`legal.${page}.intro`)}</p>

        <nav className="page-legale__sommaire" aria-label={t('legal.sommaire')}>
          <h2 className="page-legale__sommaire-titre">{t('legal.sommaire')}</h2>
          <ol>
            {sections.map((section, index) => (
              <li key={section.titre}>
                <a href={`#section-${String(index)}`}>{section.titre}</a>
              </li>
            ))}
          </ol>
        </nav>

        {/* La page cookies porte le panneau de choix : c’est là qu’il se modifie (A-41, §11). */}
        {page === 'cookies' ? <GestionCookies /> : null}

        {sections.map((section, index) => (
          <section className="page-legale__section" key={section.titre} id={`section-${String(index)}`}>
            <h2>{section.titre}</h2>
            {(section.paragraphes ?? []).map((paragraphe) => (
              <p key={paragraphe}>{paragraphe}</p>
            ))}
            {section.liste === undefined ? null : (
              <ul>
                {section.liste.map((element) => (
                  <li key={element}>{element}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </main>
      <PiedDePage />
    </div>
  );
}

/** `/mentions-legales`. */
export function EcranMentionsLegales() {
  return <PageLegale page="mentionsLegales" />;
}

/** `/cgu`. */
export function EcranCgu() {
  return <PageLegale page="cgu" />;
}

/** `/confidentialite`. */
export function EcranConfidentialite() {
  return <PageLegale page="confidentialite" />;
}

/** `/cookies`. */
export function EcranCookies() {
  return <PageLegale page="cookies" />;
}
